import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Container,
  Fade,
  Typography,
  CircularProgress,
  TextField,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/AuthContext";
import { api } from "../services/api";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AppBackground from "../components/layout/AppBackground";

const Checkout = () => {
  const [toastMessage, setToastMessage] = useState("");
  const toastTimeoutRef = useRef(null);
  const showToastMessage = (message, duration = 5000) => {
    setToastMessage(message);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage("");
      toastTimeoutRef.current = null;
    }, duration);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("checkout");
  const lang = i18n.language;
  const { user, token, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const id = searchParams.get("id");

  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const hasRenderedButton = useRef(false);

  const [discountCode, setDiscountCode] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [discountValid, setDiscountValid] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isCheckingCode, setIsCheckingCode] = useState(false);

  const isEarlyPitcher = !!user?.is_early_pitcher;
  const isUndeltaUser = user?.source === "undelta";

  useEffect(() => {
    if (!user && !token) {
      showToastMessage(t("redirecting_to_login"));
      const currentPath = location.pathname + location.search;
      setTimeout(() => {
        navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      }, 1200);
    }
  }, [user, token, navigate, location]);
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!user) return;

    if (isUndeltaUser) {
      showToastMessage(
        lang === "es"
          ? "Las compras están deshabilitadas para cuentas UNDelta."
          : "Purchases are disabled for UNDelta accounts."
      );
      setTimeout(() => {
        navigate("/pricing", { replace: true });
      }, 1500);
      return;
    }

    if (!isEarlyPitcher) {
      showToastMessage(t("only_early_pitchers"));
      setTimeout(() => {
        navigate("/early-pitchers", { replace: true });
      }, 2000);
    }
  }, [user, isAuthenticated, isEarlyPitcher, isUndeltaUser, lang, navigate]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await api.get("/api/pricing");
        const list = type === "plan" ? res.data.plans : res.data.packs;
        const found = list.find((el) => String(el.id) === id);
        setItem(found);
      } catch (error) {
        console.error("Error loading item:", error);
      } finally {
        setIsLoading(false);
      }
    };
    if (type && id) fetchData();
  }, [type, id, location.search]);

  useEffect(() => {
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) {
      if (existingScript.parentNode === document.body) {
        document.body.removeChild(existingScript);
      }
      delete window.paypal;
      setPaypalLoaded(false);
    }

    const script = document.createElement("script");
    const env = process.env.REACT_APP_PAYPAL_ENV || "sandbox";
    const clientId =
      env === "live"
        ? process.env.REACT_APP_PAYPAL_LIVE_CLIENT_ID
        : process.env.REACT_APP_PAYPAL_SANDBOX_CLIENT_ID;
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&locale=${lang === "es" ? "es_ES" : "en_US"}`;
    script.onload = () => setPaypalLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (script && script.parentNode === document.body) {
        document.body.removeChild(script);
      }
    };
  }, [lang]);

  const renderTimeout = useRef(null);
  useEffect(() => {
    if (!paypalLoaded || !item || !isAuthenticated) return;

    if (renderTimeout.current) clearTimeout(renderTimeout.current);

    renderTimeout.current = setTimeout(() => {
      const container = document.getElementById("paypal-button-container");
      if (!container) return;

      container.innerHTML = "";
      hasRenderedButton.current = false;

      const paypalButtons = window.paypal.Buttons({
        createOrder: async () => {
          const res = await api.post("/api/paypal/create-order", {
            type,
            item_id: item.id,
            discount_code: discountValid ? appliedCode : null,
          });
          return res.data.paypal_order_id;
        },
        onApprove: async (data) => {
          try {
            setIsConfirmingPayment(true);
            const res = await api.post("/api/paypal/capture-order", {
              order_id: data.orderID,
            });

            if (res.data.success) {
              navigate("/checkout/result?success=true");
            } else {
              navigate("/checkout/result?success=false", {
                state: { lastParams: location.search },
              });
              showToastMessage("❌ Payment confirmation failed");
            }
          } catch (err) {
            showToastMessage("❌ An unexpected error occurred");
          }
        },
        onCancel: () => {
          showToastMessage("⚠️ Payment was cancelled");
          navigate("/checkout/result?success=false", {
            state: { lastParams: location.search },
          });
        },
        onError: (err) => {
          console.error("PayPal error:", err);
          showToastMessage("❌ PayPal error occurred");
        },
      });

      paypalButtons.render("#paypal-button-container").catch((err) => {
        console.error("PayPal render failed:", err);
      });

      hasRenderedButton.current = true;
    }, 150);

    return () => {
      if (renderTimeout.current) clearTimeout(renderTimeout.current);
      const container = document.getElementById("paypal-button-container");
      if (container) container.innerHTML = "";
      const frames = document.querySelectorAll("iframe[src*='paypal.com']");
      frames.forEach((el) => el.remove());
    };
  }, [paypalLoaded, item, isAuthenticated, appliedCode || "", type || "", lang || ""]);

  const validateDiscount = async () => {
    if (!discountCode) return;

    setIsCheckingCode(true);
    try {
      const res = await api.get(`/api/discounts/validate?code=${discountCode}&type=${type}&id=${id}`);
      if (res.data.valid) {
        setDiscountValid(true);
        setDiscountAmount(res.data.discount_amount_usd);
        setAppliedCode(discountCode);
        showToastMessage(t("discount_applied"));
      } else {
        setDiscountValid(false);
        setDiscountAmount(0);
        setAppliedCode("");
        showToastMessage(t("invalid_code"));
      }
    } catch (err) {
      console.error("Error validating discount:", err);
      setDiscountValid(false);
      setDiscountAmount(0);
      setAppliedCode("");
      showToastMessage(t("invalid_code"));
    } finally {
      setIsCheckingCode(false);
    }
  };

  const discountedTotal = item
    ? (discountValid ? item.price_usd - discountAmount : item.price_usd)
    : 0;

  const handleConfirmFree = async () => {
    try {
      setIsConfirmingPayment(true);
      const res = await api.post("/api/paypal/free-purchase", {
        type,
        item_id: item.id,
        discount_code: appliedCode,
      });

      if (res.data.success) {
        navigate(`/checkout/result?success=true&type=${type}&free=true`);
      } else {
        navigate(`/checkout/result?success=false&type=${type}&free=true`);
      }
    } catch (err) {
      console.error("Error in free purchase:", err);
      navigate(`/checkout/result?success=false&type=${type}&free=true`);
    } finally {
      setIsConfirmingPayment(false);
    }
  };


  return (
    <Fade in timeout={700}>
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          px: 3,
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, textAlign: "center" }}>
            {t("title")}
          </Typography>
          <Box
            sx={{
              backgroundColor: "rgba(0,0,0,0.5)",
              color: "white",
              borderRadius: 4,
              boxShadow: 3,
              p: 4,
              textAlign: "center",
            }}
          >
            {item ? (
              <>
                <Typography variant="h5" gutterBottom>
                  {lang === "es" ? item.name_es : item.name_en}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  ${discountedTotal}
                </Typography>
                {type === "plan" && item.duration_months && (
                  <Typography variant="body2" sx={{ fontStyle: "italic", mb: 2 }}>
                    {item.duration_months} {t("duration_months")}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 3 }}>
                  {lang === "es" ? item.description_es : item.description_en}
                </Typography>

                <Box sx={{ mt: 2, mx: "auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 1, fontSize: "1rem", maxWidth: 300 }}>
                  <Typography variant="caption">{t("paypal_payment_info")}</Typography>
                </Box>

                <Box sx={{ mt: 5, textAlign: "center" }}>
                  <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.2)" }} />
                  <Typography variant="body2" gutterBottom>
                    {t("discount_code_title")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap", mt: 2 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      label={t("discount_code")}
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#362c41ff',
                          borderRadius: 2,
                          color: '#000',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: '#ddd', // color del texto
                        },
                        '& .MuiInputLabel-root': {
                          color: '#ddd', // color del label
                        },
                      }}
                    />
                    <Button variant="contained" onClick={validateDiscount} disabled={isCheckingCode} sx={{ borderRadius: 999 }}>
                      {t("apply")}
                    </Button>
                  </Box>
                  {discountValid && appliedCode && (
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap", mt: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          color: "lightgreen",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CheckBoxIcon fontSize="small" /> {t("discount_applied_bottom")}: <strong>{appliedCode.toUpperCase()}</strong>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress color="inherit" size={28} />
              </Box>
            )}
          </Box>

          {isLoading ? (
            <CircularProgress color="inherit" size={28} />
            ) : discountedTotal === 0 ? (
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.5)",
                color: "white",
                borderRadius: 4,
                boxShadow: 3,
                mt:2,
                p: 4,
                textAlign: "center",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                sx={{ borderRadius: 999, minWidth: 180 }}
                onClick={handleConfirmFree}
              >
                {t("confirm_free")}
              </Button>
            </Box>
            ) : (
              <Box
                key={`${lang}-${item?.id}-${appliedCode}`}
                sx={{
                  backgroundColor: "#e8d9f1ff",
                  borderRadius: 3,
                  p: 3,
                  mt: 3,
                }}
              >
                <div id="paypal-button-container" />
              </Box>
            )}
        </Container>

        {isConfirmingPayment && (
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              color: "white",
              borderRadius: "20px",
              px: 4,
              py: 3,
              zIndex: 9999,
              textAlign: "center",
              minWidth: 280,
              maxWidth: "90vw",
              boxShadow: 4,
            }}
          >
            <Typography sx={{ fontSize: "1.2rem", fontWeight: "bold", mb: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <HourglassTopIcon sx={{ fontSize: 24 }} />
              {t("confirming_payment")}
            </Typography>
            <CircularProgress size={28} sx={{ color: "white", mt: 1 }} />
          </Box>
        )}

        {toastMessage && (
          <Box
            sx={{
              position: "fixed",
              top: 200,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              px: 2,
              py: 1,
              borderRadius: 2,
              fontSize: 20,
              zIndex: 9999,
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              gap: 1,
              maxWidth: { xs: "90vw", sm: "70vw" },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2">{toastMessage}</Typography>
          </Box>
        )}
      </AppBackground>
    </Fade>
  );
};

export default Checkout;
